import React from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';
import { RequirePermissionRoute } from '../components/RequirePermissionRoute';
import { RestrictedRedirect } from '../components/RestrictedRedirect';
import { DashboardPage } from '../pages/DashboardPage';
import { WelcomePage } from '../pages/WelcomePage';
import { PeopleAccessPage } from '../pages/people/PeopleAccessPage';
import { useAuth } from '../packages/auth-client';
import { LegacyRedirect } from './LegacyRedirect';
import { BillingDashboard } from '../pages/admin/BillingDashboard';
import { AISettingsPage } from '../pages/admin/AISettingsPage';
import { TemplateMarketplacePage } from '../pages/marketplace/TemplateMarketplacePage';
import { TemplatesPage } from '../pages/TemplatesPage';
import { AccessRequestsPage } from '../pages/AccessRequestsPage';
import { EntitiesPage } from '../pages/entities/EntitiesPage';
import { EntityDetailPage } from '../pages/entities/EntityDetailPage';
import { WorkloadPage } from '../pages/workload/WorkloadPage';
import { TimelinePage } from '../pages/timeline/TimelinePage';
import { ProfilePage } from '../pages/profile/ProfilePage';
import { BetaPage } from '../pages/BetaPage';
import { ToolsPage } from '../pages/tools/ToolsPage';
import { SettingsHome } from '../pages/settings/SettingsHome';
import { WorkspaceSettings } from '../pages/settings/WorkspaceSettings';
import { DeliverySettings } from '../pages/settings/DeliverySettings';
import { MonitoringSettings } from '../pages/settings/MonitoringSettings';
import { MyWorkPage } from '../pages/work/MyWorkPage';
import { ReviewsPage } from '../pages/work/ReviewsPage';
import { ConnectionsPage } from '../pages/connections/ConnectionsPage';
import { HelpPage } from '../pages/help/HelpPage';

/** Tenant People & Access; a superadmin goes to the platform directory, query kept. */
function PeopleForViewer() {
    return useAuth().isSuperAdmin() ? <LegacyRedirect to="/admin/users" /> : <PeopleAccessPage />;
}

/** /profile, except the old API-keys tab, which now lives on /connections (query and hash kept). */
function ProfileRoute() {
    const { search, hash } = useLocation();
    const query = new URLSearchParams(search);
    if (query.get('tab') !== 'apikeys') return <ProfilePage />;
    query.delete('tab');
    const qs = query.toString();
    return <Navigate replace to={`/connections${qs ? `?${qs}` : ''}${hash}`} />;
}

/** Tenant workspace routes. Rendered inside ProtectedRoute › Layout. */
export const workspaceRoutes = (
    <>
        {/* Dashboard/Workload/Timeline are hidden from restricted
            users' sidebar — also block them by URL. */}
        <Route element={<RestrictedRedirect />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            {/* First-use checklist (U34); registration lands here. */}
            <Route path="/welcome" element={<WelcomePage />} />
        </Route>
        {/* People & Access (U24a), gated like the sidebar link. A superadmin
            is sent to the cross-tenant /admin/users in the platform console. */}
        <Route element={<RequirePermissionRoute resource="users" action="read" />}>
            <Route path="/people" element={<PeopleForViewer />} />
        </Route>
        {/* My Work + Reviews (U19): open to restricted users — the API
            scopes both lists to tasks the caller may see. */}
        <Route path="/my-work" element={<MyWorkPage />} />
        <Route path="/reviews" element={<ReviewsPage />} />
        {/* /billing is canonical: Stripe Checkout success/cancel and the
            billing portal return there (backend billing/service.go). */}
        <Route path="/billing" element={<BillingDashboard />} />
        <Route path="/admin/billing" element={<LegacyRedirect to="/billing" />} />
        <Route path="/admin/ai" element={<AISettingsPage />} />

        {/* Configuration / library menus — gated by navigation:* so
            viewers/managers can't reach them by URL unless granted. */}
        <Route element={<RequirePermissionRoute resource="navigation" action="marketplace" />}>
            <Route path="/marketplace" element={<TemplateMarketplacePage />} />
        </Route>
        <Route element={<RequirePermissionRoute resource="navigation" action="templates" />}>
            <Route path="/templates" element={<TemplatesPage />} />
        </Route>

        {/* Access requests (approver inbox) */}
        <Route element={<RequirePermissionRoute resource="navigation" action="access_requests" />}>
            <Route path="/access-requests" element={<AccessRequestsPage />} />
        </Route>

        {/* PM: Entities (Records) */}
        <Route element={<RequirePermissionRoute resource="navigation" action="records" />}>
            <Route path="/entities" element={<EntitiesPage />} />
            <Route path="/entities/:id" element={<EntityDetailPage />} />
        </Route>

        {/* PM: Workload + Timeline — hidden from restricted
            users' sidebar, blocked by URL too. */}
        <Route element={<RestrictedRedirect />}>
            <Route path="/workload" element={<WorkloadPage />} />
            <Route path="/timeline" element={<TimelinePage />} />
            {/* Tools catalogue (U26): every tool it lists is unrestricted-only. */}
            <Route path="/tools" element={<ToolsPage />} />
        </Route>

        {/* Settings & help (U16). /profile stays the personal-settings page:
            emails and the calendar OAuth callback link to /profile?tab=. */}
        <Route path="/settings" element={<SettingsHome />} />
        <Route path="/settings/workspace" element={<WorkspaceSettings />} />
        <Route path="/settings/monitoring" element={<MonitoringSettings />} />
 <Route path="/settings/delivery" element={<DeliverySettings />} />
        {/* Signed-in help (U27). /support stays the public, store-required page. */}
        <Route path="/help" element={<HelpPage />} />
        <Route path="/profile" element={<ProfileRoute />} />
        <Route path="/connections" element={<ConnectionsPage />} />
        <Route element={<RequirePermissionRoute resource="navigation" action="beta" />}>
            <Route path="/beta" element={<BetaPage />} />
        </Route>
    </>
);
